/**
 * Avviso nell'editor della **causale**: spegnere «Rifiutabile» qui non blocca soltanto il
 * rifiuto — fa approvare la richiesta al momento dell'invio.
 *
 * ## Perché serve qui e non basta l'hint del campo
 *
 * Il campo dice già «il rifiuto viene respinto dal server», che è vero e insufficiente: la
 * conseguenza vera è che con `leave.nonRejectableAutoApprove` attivo (default su tutti i
 * tenant) la richiesta **non arriva in coda a nessuno**. Chi configura una causale per
 * proteggere un permesso di legge sta anche, senza saperlo, togliendo quel caso dalla
 * revisione del responsabile.
 *
 * L'altra metà è la regola di composizione: il server calcola
 * `lt.IsRejectable && reason.IsRejectable`, quindi **basta uno dei due spento**. Una causale
 * non rifiutabile dentro un tipo rifiutabile rende non rifiutabile la richiesta — ed è il
 * caso che il documento dei gap descriveva al contrario («la causale vince sul tipo»).
 *
 * Gemello di `LeaveAutoApproveNotice`, che fa lo stesso lavoro sul modulo del tipo. Due
 * componenti e non uno perché i due moduli hanno campi diversi: qui c'è un flag solo, e un
 * avviso che parlasse di `autoApprovePolicy` citerebbe un campo che in questa modale non c'è.
 */
import { Alert, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTenantConfig } from '@/shared/hooks/useTenantConfig';
import { nonRejectableReasonNotice } from './nonRejectable';

export function LeaveReasonRejectNotice({ values }: { values: Record<string, unknown> }) {
  const cfg = useTenantConfig();
  /* Default `true` come nell'avviso del tipo: è il valore su tutti i tenant dev, e finché la
     config non è caricata tacere sarebbe la scelta peggiore — l'avviso serve a chi sta
     configurando in quel momento. */
  const autoApprove = cfg.getBool('leave.nonRejectableAutoApprove', true);

  const message = nonRejectableReasonNotice(
    values.isRejectable === false ? false : values.isRejectable === true ? true : undefined,
    autoApprove,
  );
  if (!message) return null;

  /* Le due parti in **grassetto** del testo puro arrivano come `**…**`: qui si rendono come
     testo semplice invece di interpretarle, perché un mini-parser di markdown per due parole
     è più codice di quanto valga. Il senso della frase non dipende dall'enfasi. */
  return (
    <Alert variant="light" color="yellow" icon={<IconInfoCircle size={16} />} styles={{ message: { fontSize: 'var(--fs-compact)' } }}>
      <Text fz="xs" c="var(--ink-700)">{message.replace(/\*\*/g, '')}</Text>
    </Alert>
  );
}
